// Home and town growth at day boundaries. Solvability check arrives in slice 4.
import { dist, isBuildable } from './geometry';
import { nextFloat } from './rng';
import type { Balance, Home, Id, MapData, Town, World } from './types';

export function townId(town: Town): Id {
  return 'town:' + town.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

export function newId(world: World, prefix: string): Id {
  const id = `${prefix}${world.nextId}`;
  world.nextId += 1;
  return id;
}

/** Try to place one home near (cx, cy). Returns false if no legal spot was found. */
export function placeHomeNear(
  world: World,
  map: MapData,
  balance: Balance,
  cx: number,
  cy: number,
  town: Id,
  radius: number,
): Home | undefined {
  for (let attempt = 0; attempt < 12; attempt++) {
    const angle = nextFloat(world.rng) * Math.PI * 2;
    const r = radius * Math.sqrt(nextFloat(world.rng));
    const x = Math.round(cx + Math.cos(angle) * r);
    const y = Math.round(cy + Math.sin(angle) * r);
    if (!isBuildable(x, y, map)) continue;
    let clear = true;
    for (const h of world.homes) {
      if (dist(h.x, h.y, x, y) < balance.homes.minSpacing) {
        clear = false;
        break;
      }
    }
    if (!clear) continue;
    const home: Home = { id: newId(world, 'h'), x, y, townId: town, status: 'starved', outage: 0 };
    world.homes.push(home);
    return home;
  }
  return undefined;
}

export function spawnTownHomes(
  world: World,
  map: MapData,
  balance: Balance,
  town: Town,
  n: number,
): void {
  const id = townId(town);
  // Grow outward as the town fills so it never jams.
  for (let i = 0; i < n; i++) {
    const existing = world.homes.filter((h) => h.townId === id).length;
    const radius = map.spawn.clusterRadius * (0.4 + 0.6 * Math.sqrt(existing / 20));
    placeHomeNear(world, map, balance, town.x, town.y, id, radius);
  }
}

/** Daily growth: compounding on the current home count, plus a new town on schedule. */
export function spawnDaily(world: World, map: MapData, balance: Balance): void {
  const live = world.homes.filter((h) => h.status !== 'dark');
  if (live.length === 0) return;

  world.growthCarry += live.length * map.spawn.growthPerDay;
  let n = Math.floor(world.growthCarry);
  world.growthCarry -= n;

  const towns = new Map<Id, Town>();
  for (const t of map.towns) towns.set(townId(t), t);

  // Weighted by the town's own weight and how big it already is: big cities keep
  // growing, and the map's weights decide which cities those are.
  const open: { town: Town; id: Id; count: number }[] = [];
  for (const t of map.towns) {
    const id = townId(t);
    const count = world.homes.filter((h) => h.townId === id).length;
    if (count > 0) open.push({ town: t, id, count });
  }
  let totalWeight = 0;
  for (const o of open) totalWeight += (o.town.weight ?? 1) * o.count;

  while (n > 0 && open.length > 0 && totalWeight > 0) {
    n--;
    let roll = nextFloat(world.rng) * totalWeight;
    let chosen = open[open.length - 1];
    for (const o of open) {
      roll -= (o.town.weight ?? 1) * o.count;
      if (roll <= 0) {
        chosen = o;
        break;
      }
    }
    if (!chosen) break;
    const radius = map.spawn.clusterRadius * (0.4 + 0.6 * Math.sqrt(chosen.count / 20));
    placeHomeNear(world, map, balance, chosen.town.x, chosen.town.y, chosen.id, radius);
  }

  if (world.time.day % map.spawn.newTownEveryDays === 0) {
    const opened = new Set(world.homes.map((h) => h.townId));
    const next = map.towns.find((t) => !opened.has(townId(t)));
    if (next) spawnTownHomes(world, map, balance, next, balance.homes.newTownHomes);
  }
}
